/**
 * Office provider self-heal (#633).
 *
 * `VITE_OFFICE_PROVIDER` is a build-time constant: the office editor
 * registration is chosen from it when the bundle is built
 * (./editors/office-registration.ts). A tab that outlives a provider-change
 * deploy (collabora ⇄ onlyoffice) keeps running the stale bundle, mounts the
 * WRONG office editor, and dead-ends on "Unable to open this file" until the
 * user manually hard-refreshes — which we cannot expect users to know to do.
 *
 * This compares the bundle's build provider to the deployment's CURRENT provider
 * (`GET /api/public/office-config`) and, on a mismatch, does ONE guarded reload
 * to pull the fresh bundle. The whole thing is a no-op unless a provider change
 * actually shipped under a live tab, and it fails SAFE — an unreachable endpoint
 * leaves current behaviour untouched.
 */
import { request } from '@papan/api-client'
import { OFFICE_PROVIDER } from './editors/docsapi-provider.js'

export interface OfficeSelfHealDeps {
  /** The provider this bundle was BUILT for (VITE_OFFICE_PROVIDER). */
  buildProvider: string
  /** The deployment's CURRENT provider, or null when it can't be resolved. */
  fetchServerProvider: () => Promise<string | null>
  /** Pull the fresh bundle. */
  reload: () => void
  /** Have we already reloaded once for this mismatch? (loop guard) */
  hasReloaded: () => boolean
  /** Remember that we reloaded, so the post-reload load can't loop. */
  markReloaded: () => void
  /** Clear the loop guard once a match is observed, so a LATER second provider
   *  flip in the same long-lived session still heals. */
  clearReloaded: () => void
}

export type OfficeSelfHealOutcome = 'match' | 'reloaded' | 'suppressed-loop' | 'unknown'

/**
 * Pure core — dependency-injected so the reload decision is testable without a
 * DOM. Mark BEFORE reloading: the reload re-runs this on the fresh load, and if
 * the build/env is genuinely misconfigured (bundle and server truly disagree)
 * the flag is what stops an infinite reload loop — it degrades to "one wasted
 * reload", then the normal editor error surfaces (and the #633 beacon fires).
 */
export async function ensureOfficeProviderFresh(deps: OfficeSelfHealDeps): Promise<OfficeSelfHealOutcome> {
  let server: string | null
  try {
    server = await deps.fetchServerProvider()
  } catch {
    return 'unknown'
  }
  if (!server) return 'unknown'
  if (server === deps.buildProvider) {
    // A match means the bundle is current — retire a stale loop-guard flag from a
    // PRIOR heal, so a second genuine flip later in this session isn't suppressed.
    if (deps.hasReloaded()) deps.clearReloaded()
    return 'match'
  }
  if (deps.hasReloaded()) return 'suppressed-loop'
  deps.markReloaded()
  deps.reload()
  return 'reloaded'
}

const RELOAD_FLAG = 'papan:office-provider-selfheal-reloaded'

/**
 * Wired entry point — call once at SPA boot. A stale tab is behind the WHOLE
 * deploy (a provider flip rebuilds the whole SPA), so healing at boot fixes more
 * than office and gets the user onto the current bundle before they ever open a
 * file. Every dependency is wrapped so a hostile/absent `sessionStorage` (private
 * mode, blocked storage) can't turn the self-heal itself into an error.
 */
export function runOfficeProviderSelfHeal(): Promise<OfficeSelfHealOutcome> {
  return ensureOfficeProviderFresh({
    buildProvider: OFFICE_PROVIDER,
    fetchServerProvider: async () => {
      const res = await request<{ provider?: string }>('/api/public/office-config').catch(() => null)
      return res?.provider ?? null
    },
    reload: () => { try { window.location.reload() } catch { /* non-browser */ } },
    hasReloaded: () => { try { return sessionStorage.getItem(RELOAD_FLAG) === '1' } catch { return true } },
    markReloaded: () => { try { sessionStorage.setItem(RELOAD_FLAG, '1') } catch { /* ignore */ } },
    clearReloaded: () => { try { sessionStorage.removeItem(RELOAD_FLAG) } catch { /* ignore */ } },
  })
}

/**
 * Is this the office editor (the surface that dead-ends on a provider mismatch)?
 * The office registrations id as `<provider>-<sheet|doc|slide>` for each engine
 * (features/collab/client/editors/{onlyoffice,collabora}-registration.ts). Used
 * to trigger the self-heal when an office file is opened in an ALREADY-loaded tab
 * — the case boot-time healing can't reach, because such a tab never re-runs
 * boot until it reloads.
 */
export function isOfficeEditorId(id: string | undefined): boolean {
  return !!id && /^(collabora|onlyoffice|eurooffice)-(sheet|doc|slide)$/.test(id)
}
